import { type StoreControl, TimeUnitMultiplier } from 'commons';
import { isDate } from 'date-fns';
import type { SeriesD, SeriesInside } from '../types/chart.types';

/**
 * Fills the given series with null values to match the specified resolution.
 *
 * @param series - An array of promises that resolve to SeriesInside arrays or any other type.
 * @param control - An object containing the control parameters for the series.
 * @returns A promise that resolves to an array of SeriesD, with null values added to match the resolution.
 *
 * The function performs the following steps:
 * 1. If the series array is empty, it returns an empty array.
 * 2. Initializes an empty array `addArray` to store the new series with null values.
 * 3. Waits for all promises in the series array to resolve.
 * 4. Calculates the time difference between consecutive series and adds null values if the difference exceeds the specified window size.
 * 5. Returns the combined array of original and new series, sorted by time.
 */
export const fillSeriesToResolutionWithNull = async (series: Promise<SeriesInside[]>[] | SeriesInside[][] | SeriesD, control: StoreControl) => {
  if (series.length === 0) {
    return [];
  }

  const addArray: SeriesD = [];

  const filledData = await Promise.all(series.map((s) => Promise.resolve(s)));
  const multiplier = Number(TimeUnitMultiplier[control.windowUnit]);
  // biome-ignore lint/suspicious/useIterableCallbackReturn: spec
  filledData.flatMap((inside, i, all) => {
    const currentTime = inside[0] instanceof Date ? inside[0].getTime() : 0;
    const nextTime = all[i + 1] && all[i + 1][0] instanceof Date ? (all[i + 1][0] as Date).getTime() : Number.NaN;
    const diff = nextTime - currentTime;
    if (diff > control.windowSize * multiplier) {
      addArray.push([new Date(currentTime + control.windowSize * multiplier), null, null]);
    }
  });
  return [...filledData, ...addArray].sort((a, b) => {
    if (isDate(a[0]) && isDate(b[0])) {
      return a[0].getTime() - b[0].getTime();
    } else {
      return new Date(a[0] as number).getTime() - new Date(b[0] as number).getTime();
    }
  });
};
